import React, { useEffect, useState } from 'react';
import { CircleCheck, LoaderCircle } from 'lucide-react';
import Block from './Block';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';

const DONE_STAGES = new Set(['complete', 'completed', 'done', 'finished', 'ready']);

// The clean phase reports no progress of its own and usually takes about 10s,
// so the bar is paced to that: 0 -> 96% over the first 10s, then the last 4%
// stretched over the next 50s so it never stalls before the phase ends. When
// the phase completes the bar runs from wherever it is to 100% in one second
// rather than jumping.
const FAST_PCT = 96;
const FAST_MS = 10_000;
const SLOW_MS = 50_000;
const FINISH_MS = 1_000;
const TICK_MS = 100;

const pacedProgress = (elapsedMs) => {
  if (elapsedMs <= FAST_MS) return FAST_PCT * (elapsedMs / FAST_MS);
  return Math.min(100, FAST_PCT + (100 - FAST_PCT) * ((elapsedMs - FAST_MS) / SLOW_MS));
};

// Synthetic progress while the clean phase runs. `live` is
// { startedAt: ms | null, done: bool } from the page's phase tracking;
// null when the block is history. Returns null when there is nothing to
// synthesise (not live, or the finish run has played out), so the persisted
// data wins.
const useLiveProgress = (live) => {
  const startedAt = live ? live.startedAt : null;
  const done = Boolean(live && live.done);
  const [now, setNow] = useState(() => Date.now());
  // Where the bar was when the phase completed, and when: the finish run
  // starts from there. Reset whenever a new run starts.
  const [finish, setFinish] = useState(null); // { at, from }
  const paced = startedAt ? pacedProgress(now - startedAt) : 0;
  useEffect(() => { setFinish(null); }, [startedAt]);
  useEffect(() => {
    if (done && !finish) setFinish({ at: Date.now(), from: paced });
    // `paced` is read once, at the moment the phase completes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done, finish]);
  const finishing = Boolean(finish) && now - finish.at < FINISH_MS;
  const running = Boolean(startedAt) && (!done || finishing);
  useEffect(() => {
    if (!running) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, [running, startedAt]);
  if (!live) return null;
  if (done) {
    if (!finish || !finishing) return null;
    const t = (now - finish.at) / FINISH_MS;
    return { progress: finish.from + (100 - finish.from) * t, stage: 'Cleaning' };
  }
  if (!startedAt) return { progress: 0, stage: 'Queued' };
  return { progress: paced, stage: 'Cleaning' };
};

/**
 * Renders the `preprocessing_progress` block surfaced after a file upload —
 * morphs into `dataset_summary` when the background preprocessor finishes.
 *
 * The persisted block is already at 100%: the backend writes it when
 * preprocessing finishes, right as the clean phase starts, and that phase
 * emits nothing until it is done. While it runs, `live` (see
 * ChatAnalysisPage) paces the bar instead; the moment the phase completes
 * the persisted 100% shows.
 */
const PreprocessingProgress = ({ blockData = {}, live = null }) => {
  const synthetic = useLiveProgress(live);
  const progress = synthetic ? synthetic.progress : Number(blockData.progress || 0);
  const stage = synthetic ? synthetic.stage : (blockData.stage || 'Queued');
  const done = !synthetic && (DONE_STAGES.has(String(stage).toLowerCase()) || progress >= 100);

  return (
    <Block
      icon="sparkles"
      title="Cleaning your data"
      description={done ? 'Your data is ready.' : 'This usually takes under a minute.'}
      badge={done ? (
        <Badge variant="success"><CircleCheck aria-hidden />Complete</Badge>
      ) : (
        <Badge variant="outline">
          <LoaderCircle aria-hidden className="q-rotate" />
          {String(stage).charAt(0).toUpperCase() + String(stage).slice(1)}
        </Badge>
      )}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Progress
          value={done ? 100 : progress}
          indeterminate={!done && progress === 0}
          variant={done ? 'success' : undefined}
        />
        <span className="q-num q-muted" style={{ fontSize: 14, minWidth: 40, textAlign: 'right' }}>
          {done ? '100%' : progress > 0 ? `${Math.round(progress)}%` : '—'}
        </span>
      </div>
    </Block>
  );
};

export default PreprocessingProgress;
